import type { SaveUserPayload } from '../../Services/serviceURL';
import { saveUser } from '../../Services/serviceURL';
import type { AddUserFormData, CustomerData, SelectionMap } from './addUserTypes';
import { buildKnownPlantIdSet } from './addUserPlantIds';

export function getSelectedPlantIds(
    selectedItems: SelectionMap,
    customersAndSites: CustomerData[]
): string[] {
    const knownPlantIds = buildKnownPlantIdSet(customersAndSites);
    const selectedPlantIds: string[] = [];
    for (const [key, isSelected] of Object.entries(selectedItems)) {
        if (isSelected && knownPlantIds.has(key)) {
            selectedPlantIds.push(key);
        }
    }
    return selectedPlantIds;
}

export type PersistUserResult =
    | { outcome: 'success'; savedUserId?: string }
    | { outcome: 'failure'; message: string; emailAlreadyExists: boolean };

export async function persistAddUser(
    formData: AddUserFormData,
    selectedItems: SelectionMap,
    customersAndSites: CustomerData[],
    isEditMode: boolean,
    userId?: string
): Promise<PersistUserResult> {
    const selectedPlantIds = getSelectedPlantIds(selectedItems, customersAndSites);
    const payload = buildSaveUserPayload(formData, selectedPlantIds, isEditMode, userId);
    const response = await saveUser(payload, formData.userType);

    if (response.Success) {
        return { outcome: 'success', savedUserId: response.Data?.id };
    }

    const message = response.Message || `Failed to ${isEditMode ? 'update' : 'save'} user`;
    return {
        outcome: 'failure',
        message,
        emailAlreadyExists: isEmailAlreadyExistsMessage(response.Message),
    };
}

export function buildSaveUserPayload(
    formData: AddUserFormData,
    selectedPlantIds: string[],
    isEditMode: boolean,
    userId?: string
): SaveUserPayload {
    return {
        id: isEditMode ? userId || '' : '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.ssoEnabled ? '' : formData.password,
        mobilePhoneCode: formData.mobilePhoneCode,
        mobilePhoneNo: formData.mobilePhone,
        officePhoneCode: formData.officePhoneCode,
        officePhoneNo: formData.officePhone,
        type: formData.userType,
        role: formData.role,
        status: formData.status,
        isSsoEnabled: formData.ssoEnabled,
        accessVisibility: selectedPlantIds,
        customerId: formData.userType === 'External' ? formData.customerId : undefined,
        salesDistrict: formData.salesDistrict ?? '',
        salesRegion: formData.salesRegion ?? '',
    };
}

export function extractSaveUserErrorMessage(error: unknown, isEditMode: boolean): string {
    const fallback = `Failed to ${isEditMode ? 'update' : 'save'} user. Please try again.`;
    if (!error || typeof error !== 'object') {
        return fallback;
    }

    const err = error as { response?: { data?: unknown }; message?: string };
    const data = err.response?.data;

    if (data && typeof data === 'object') {
        const record = data as { Message?: string; message?: string };
        if (record.Message) {
            return record.Message;
        }
        if (record.message) {
            return record.message;
        }
    }

    if (typeof data === 'string') {
        return data;
    }

    if (err.message) {
        return err.message;
    }

    return fallback;
}

export function isEmailAlreadyExistsMessage(message: string | undefined): boolean {
    return Boolean(message && message.toLowerCase().includes('email already exists'));
}
